import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { SplitAuthLayout, MarketingBenefits } from '../../shared/components/layout/SplitAuthLayout';
import { PasswordField } from '../../shared/components/ui/PasswordField';
// Google OAuth temporarily disabled until OAUTH_GOOGLE_* env vars are
// wired up on test + prod (Jenkins secret files). Re-enable this import
// and the <SocialAuthButtons /> below once the backend is configured.
// import { SocialAuthButtons } from '../../shared/components/ui/SocialAuthButtons';

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      notify.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (err) {
      notify.error(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="customer"
      marketing={
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2 }}>
            {t('auth.marketing.customerTitle')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'inherit', opacity: 0.92, fontSize: '1.0625rem' }}
          >
            {t('auth.marketing.customerSubtitle')}
          </Typography>
          <MarketingBenefits
            items={[
              t('auth.marketing.customerBenefit1'),
              t('auth.marketing.customerBenefit2'),
              t('auth.marketing.customerBenefit3'),
            ]}
          />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('auth.welcomeBack')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {t('auth.loginTitle')}
      </Typography>

      {/* Google OAuth disabled until backend env vars are set (see import above). */}
      {/* <SocialAuthButtons variant="customer" /> */}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
            placeholder="vous@exemple.com"
          />
          <PasswordField
            label={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="current-password"
            placeholder="••••••••"
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            fullWidth
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? t('common.loading') : t('auth.login')}
          </Button>
        </Stack>
      </Box>

      <Stack spacing={1.25} sx={{ mt: 3, textAlign: 'center' }}>
        <Link component={RouterLink} to="/forgot-password" sx={{ color: 'primary.main' }}>
          {t('auth.forgotPassword')}
        </Link>
        <Typography variant="body2" color="text.secondary">
          {t('auth.noAccount')}{' '}
          <Link
            component={RouterLink}
            to="/register"
            sx={{ color: 'primary.main', fontWeight: 600 }}
          >
            {t('auth.createAccount')}
          </Link>
        </Typography>
      </Stack>

      {/* Cross-promo to pro flow */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('auth.areYouAPro')}{' '}
          <Link
            component={RouterLink}
            to="/pro/login"
            sx={{ color: 'primary.main', fontWeight: 600 }}
          >
            {t('auth.proLoginLink')}
          </Link>
        </Typography>
      </Box>
    </SplitAuthLayout>
  );
}
